/** Extract a Vimeo video id (and optional privacy hash) from a URL or bare id. */
export interface VimeoRef {
  id: string;
  hash?: string;
}

export function parseVimeo(input: string): VimeoRef | null {
  const s = input.trim();
  if (!s) return null;
  if (/^\d+$/.test(s)) return { id: s };

  try {
    const url = new URL(s);
    const host = url.hostname.replace(/^www\./, "");
    if (host === "player.vimeo.com") {
      const m = url.pathname.match(/\/video\/(\d+)/);
      if (m) return { id: m[1], hash: url.searchParams.get("h") || undefined };
    }
    if (host.endsWith("vimeo.com")) {
      // /<id>, /<id>/<hash>, /channels/x/<id>, /groups/x/videos/<id>
      const parts = url.pathname.split("/").filter(Boolean);
      const idx = parts.findIndex((p) => /^\d+$/.test(p));
      if (idx >= 0) {
        const id = parts[idx];
        const next = parts[idx + 1];
        const hash =
          next && /^[a-zA-Z0-9]+$/.test(next) ? next : url.searchParams.get("h") || undefined;
        return { id, hash };
      }
    }
  } catch {
    return null;
  }
  return null;
}

/* ------------------------------ Vimeo Player SDK ------------------------------ */

export interface VimeoPlayer {
  play(): Promise<void>;
  pause(): Promise<void>;
  setCurrentTime(seconds: number): Promise<number>;
  destroy(): Promise<void>;
  on(event: string, callback: (...args: unknown[]) => void): void;
  enableTextTrack(language: string): Promise<unknown>;
}

export interface VimeoSDK {
  Player: new (el: HTMLElement, options: Record<string, unknown>) => VimeoPlayer;
}

declare global {
  interface Window {
    Vimeo?: VimeoSDK;
  }
}

/** After a failed load, calls within this window fail fast instead of adding
 *  another script tag — an offline kiosk would otherwise retry on every slide. */
export const VIMEO_RETRY_COOLDOWN_MS = 60_000;

/** How long a script load may take before it counts as failed. */
const LOAD_TIMEOUT_MS = 20_000;

let sdkPromise: Promise<VimeoSDK> | null = null;
let lastFailureAt: number | null = null;

/** Lazily load the Vimeo Player SDK (once). A failed load is NOT cached
 *  forever, so a kiosk that boots before its network is up retries on the
 *  first Vimeo slide after the cooldown. */
export function loadVimeoSdk(): Promise<VimeoSDK> {
  if (sdkPromise) return sdkPromise;
  if (lastFailureAt !== null && Date.now() - lastFailureAt < VIMEO_RETRY_COOLDOWN_MS) {
    return Promise.reject(new Error("Vimeo sdk failed recently"));
  }
  const pending = new Promise<VimeoSDK>((resolve, reject) => {
    if (typeof window === "undefined") {
      reject(new Error("no window"));
      return;
    }
    if (window.Vimeo?.Player) {
      resolve(window.Vimeo);
      return;
    }
    const existing = document.querySelector<HTMLScriptElement>("script[data-vimeo-sdk]");
    const tag = existing ?? document.createElement("script");
    const fail = (message: string) => {
      clearTimeout(timeout);
      // Remove the tag so a retry appends a fresh one. A tag that loaded but
      // left no window.Vimeo would otherwise be reused, and its "load" event
      // never fires again — every later retry would hang.
      tag.remove();
      reject(new Error(message));
    };
    const timeout = setTimeout(() => fail("Vimeo sdk load timed out"), LOAD_TIMEOUT_MS);
    const onload = () => {
      if (window.Vimeo) {
        clearTimeout(timeout);
        resolve(window.Vimeo);
      } else {
        fail("Vimeo failed");
      }
    };
    if (existing) {
      existing.addEventListener("load", onload);
      existing.addEventListener("error", () => fail("Vimeo sdk failed"));
      return;
    }
    tag.src = "https://player.vimeo.com/api/player.js";
    tag.setAttribute("data-vimeo-sdk", "");
    tag.onload = onload;
    tag.onerror = () => fail("Vimeo sdk failed");
    document.head.appendChild(tag);
  });
  sdkPromise = pending;
  pending.then(
    () => {
      lastFailureAt = null;
    },
    () => {
      lastFailureAt = Date.now();
      if (sdkPromise === pending) sdkPromise = null;
    },
  );
  return pending;
}
